import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Sparkles, Clock, Share2, ChevronUp, ChevronDown, ImagePlus, Loader2, Construction } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { photoUrl } from "@/components/site/Menus";
import { Switch } from "@/components/ui/switch";

const inp = "w-full bg-background border-2 border-foreground rounded-lg px-4 py-3 outline-none focus:bg-primary/10";
const EMPTY_SOCIALS = { facebook: "", instagram: "", google: "" };

export default function ContentEditor() {
  const [specials, setSpecials] = useState([]);
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");
  const [openingHours, setOpeningHours] = useState("");
  const [socials, setSocials] = useState(EMPTY_SOCIALS);
  const [storyImage, setStoryImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comingSoon, setComingSoon] = useState(false);
  const [modeSaving, setModeSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/content").then(({ data }) => {
      setSpecials(data.specials || []);
      setHeading(data.story_heading || "");
      setBody(data.story_body || "");
      setStoryImage(data.story_image || "");
      setOpeningHours(data.opening_hours || "");
      setSocials({ ...EMPTY_SOCIALS, ...(data.socials || {}) });
      setComingSoon(!!data.coming_soon);
    }).catch(() => {});
  }, []);

  const toggleMode = async (val) => {
    setModeSaving(true);
    try {
      await api.put("/admin/site-mode", { coming_soon: val });
      setComingSoon(val);
      toast.success(val ? "Public now sees the “Coming Soon” page" : "Website is now LIVE to the public 🎉");
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setModeSaving(false); }
  };

  const uploadStoryImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/content/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStoryImage(data.path);
      toast.success("Photo uploaded — remember to Save content");
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", {
        specials: specials.filter((s) => s.trim()),
        story_heading: heading, story_body: body, story_image: storyImage,
        opening_hours: openingHours, socials,
      });
      toast.success("Content saved & live on the site");
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const setSocial = (k) => (e) => setSocials((s) => ({ ...s, [k]: e.target.value }));

  const moveSpecial = (i, dir) => setSpecials((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  return (
    <div className="grid lg:grid-cols-2 gap-8 items-start max-w-5xl">
      <div className={`lg:col-span-2 border-2 border-foreground rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${comingSoon ? "bg-primary/15" : "bg-muted"}`} data-testid="coming-soon-card">
        <div>
          <p className="overline text-secondary flex items-center gap-2 mb-1"><Construction size={14} /> Website mode</p>
          <p className="text-sm text-foreground/60 max-w-xl">
            When <strong>Coming Soon</strong> is on, the public sees a splash page. You (while logged in) still see the full
            site here and on the homepage, so you can edit and check everything in the background. Flip this off to go live — no redeploy needed.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`font-head font-bold text-sm ${comingSoon ? "text-secondary" : "text-foreground/60"}`}>
            {comingSoon ? "Coming Soon" : "Live"}
          </span>
          <Switch checked={comingSoon} onCheckedChange={toggleMode} disabled={modeSaving} data-testid="coming-soon-switch" />
        </div>
      </div>

      <div className="bg-muted border-2 border-foreground rounded-2xl p-6" data-testid="specials-editor">
        <p className="overline text-secondary flex items-center gap-2 mb-4"><Sparkles size={14} /> Specials &amp; Events</p>
        <p className="text-sm text-foreground/60 mb-4">Shown on the opening screen of the website. Add, remove or reorder lines — the order here is the order shown.</p>
        <div className="space-y-2">
          {specials.map((s, i) => (
            <div key={i} className="flex gap-2 items-stretch">
              <div className="flex flex-col justify-center shrink-0">
                <button type="button" onClick={() => moveSpecial(i, -1)} disabled={i === 0} data-testid={`special-up-${i}`}
                  aria-label="Move up" className="px-1.5 py-0.5 border-2 border-foreground rounded-t-md hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed"><ChevronUp size={14} /></button>
                <button type="button" onClick={() => moveSpecial(i, 1)} disabled={i === specials.length - 1} data-testid={`special-down-${i}`}
                  aria-label="Move down" className="px-1.5 py-0.5 border-2 border-t-0 border-foreground rounded-b-md hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed"><ChevronDown size={14} /></button>
              </div>
              <input className={inp} value={s} data-testid={`special-${i}`}
                onChange={(e) => setSpecials((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))}
                placeholder="e.g. Friday: Live music from 7pm" />
              <button onClick={() => setSpecials((arr) => arr.filter((_, j) => j !== i))} data-testid={`special-del-${i}`}
                className="p-3 border-2 border-destructive text-destructive rounded-lg hover:bg-destructive hover:text-destructive-foreground shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button onClick={() => setSpecials((arr) => [...arr, ""])} data-testid="special-add"
          className="mt-3 w-full flex items-center justify-center gap-2 font-head font-bold border-2 border-dashed border-foreground rounded-lg py-2.5 hover:bg-background"><Plus size={16} /> Add special / event</button>
      </div>

      <div className="bg-muted border-2 border-foreground rounded-2xl p-6" data-testid="story-editor">
        <p className="overline text-secondary mb-4">Our family story</p>
        <label className="block mb-4">
          <span className="overline text-foreground/70">Heading</span>
          <input className={`${inp} mt-2`} value={heading} onChange={(e) => setHeading(e.target.value)} data-testid="story-heading-input" placeholder="This is us" />
        </label>
        <label className="block">
          <span className="overline text-foreground/70">Story</span>
          <textarea className={`${inp} mt-2`} rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="story-body-input"
            placeholder="Tell your family story… (blank lines start a new paragraph)" />
        </label>
        <div className="mt-4">
          <span className="overline text-foreground/70">Photo</span>
          <div className="mt-2 flex items-center gap-4">
            <img src={storyImage ? photoUrl(storyImage) : "/team.webp"} alt="Story"
              className="w-24 h-28 object-cover object-top rounded-xl border-2 border-foreground shrink-0" data-testid="story-image-preview" />
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadStoryImage} data-testid="story-image-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="story-image-upload"
                className="flex items-center gap-2 font-head font-bold text-sm border-2 border-foreground rounded-full px-4 py-2 hover:bg-background disabled:opacity-60">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} {uploading ? "Uploading…" : "Change photo"}
              </button>
              {storyImage && (
                <button type="button" onClick={() => setStoryImage("")} data-testid="story-image-remove"
                  className="block text-sm text-destructive underline">Reset to default</button>
              )}
              <p className="text-xs text-foreground/50">JPG/PNG/WebP, up to 8MB. Then hit Save content.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-muted border-2 border-foreground rounded-2xl p-6" data-testid="hours-editor">
        <p className="overline text-secondary flex items-center gap-2 mb-4"><Clock size={14} /> Opening hours</p>
        <p className="text-sm text-foreground/60 mb-4">Shown in the website footer.</p>
        <input className={inp} value={openingHours} onChange={(e) => setOpeningHours(e.target.value)} data-testid="opening-hours-input"
          placeholder="e.g. 9am – 10pm, every day" />
      </div>

      <div className="bg-muted border-2 border-foreground rounded-2xl p-6" data-testid="socials-editor">
        <p className="overline text-secondary flex items-center gap-2 mb-4"><Share2 size={14} /> Social links</p>
        <p className="text-sm text-foreground/60 mb-4">Floating buttons on the website. Leave blank to hide a platform.</p>
        <div className="space-y-3">
          {["facebook", "instagram", "google"].map((k) => (
            <label key={k} className="block">
              <span className="overline text-foreground/70 capitalize">{k === "google" ? "Google review link" : k}</span>
              <input className={`${inp} mt-2`} value={socials[k]} onChange={setSocial(k)} data-testid={`social-${k}-input`}
                placeholder={k === "google" ? "https://g.page/r/...  (Google review link)" : `https://${k}.com/harbourhaze`} />
            </label>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2">
        <button onClick={save} disabled={saving} data-testid="content-save"
          className="flex items-center gap-2 font-head font-bold bg-secondary text-secondary-foreground border-2 border-foreground rounded-full px-6 py-3 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
          <Save size={16} /> {saving ? "Saving…" : "Save content"}
        </button>
      </div>
    </div>
  );
}
